import {
  faAirFreshener,
  faBook,
  faBookOpenReader,
  faCheck,
  faCrown,
  faEye,
  faGear,
  faHome,
  faListCheck,
  faSyncAlt,
  faUser,
  faUserGraduate,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group } from '@sparx/api/apis/sparx/teacherportal/groupsapi/v1/groupsapi';
import classNames from 'classnames';
import { useAlert, useClearAlert } from 'components/alert/alert';
import { ConfirmAlert } from 'components/alert/confirm-alert';
import { Link } from 'components/link';
import { useSelectedGroups } from 'hooks/selected-groups';
import { useWondeImportEnabled } from 'hooks/wonde-import-enabled';
import { useEnglishStudentGroups } from 'queries/management';
import { useUser } from 'queries/session';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { Flags, useBooleanFlag, useFlag } from 'utils/featureflags';
import { getStudentGroupId } from 'utils/groups';
import { useHoverClickMenu } from 'utils/hooks';
import { View } from 'views';
import {
  CollapsibleSidebar,
  SidebarPrimaryButton,
  SidebarPrimaryLink,
} from 'views/teacher/components/collapsible-sidebar';
import { ReactComponent as HandInIcon } from 'views/teacher/components/icons/nav_handin.svg';
import { ReactComponent as InsightsIcon } from 'views/teacher/components/icons/nav_insights.svg';
import { ReactComponent as MoreIcon } from 'views/teacher/components/icons/nav_more.svg';
import { ReactComponent as ReportsIcon } from 'views/teacher/components/icons/nav_reports.svg';
import { useSparxStaffFeatures } from 'views/teacher/components/sparx-staff-features-provider';
import styles from 'views/teacher/components/teacher-sidebar.module.css';
import { pathForView } from 'views/views';

export const groupPages = new Set([
  '/teacher/classes',
  '/teacher/classes/homework',
  '/teacher/classes/classwork',
  '/teacher/classes/students',
  '/teacher/classes/insights',
  '/teacher/classes/readingtest',
  '/teacher/classes/bookmarks',
  '/teacher/leaderboard',
]);

export const isGroupContextPage = (page?: string) => groupPages.has(page || '');

export const TeacherSidebar = () => {
  const [selectedGroups] = useSelectedGroups();
  const { data: groups } = useEnglishStudentGroups();

  // We don't display MIS tutor groups here.
  const location = useLocation();
  const user = useUser();

  const starredGroups = new Set(
    groups?.filter(g => g.staff.find(s => s.staffID === user?.userId)).map(g => g.name),
  );

  const isTeacherLibraryEnabled = useFlag(Flags.TeacherLibrary, false);
  const { sparxStaffFeaturesEnabled } = useSparxStaffFeatures();

  const selectingGroup = groupPages.has(location.pathname);
  const toggleGroups = location.pathname === '/teacher/live';
  const showGroups = selectingGroup || toggleGroups;

  const getGroupLink = (group: Group) => {
    if (selectingGroup) {
      return `${
        isGroupContextPage(location.pathname) ? location.pathname : '/teacher/classes/homework'
      }?group=${getStudentGroupId(group)}`;
    } else if (toggleGroups) {
      let newSelectedGroups = [...selectedGroups];
      if (selectedGroups.find(g => g.name === group.name)) {
        newSelectedGroups = selectedGroups.filter(g => g.name !== group.name);
      } else {
        newSelectedGroups.push(group);
      }
      const groupIds = newSelectedGroups.map(g => getStudentGroupId(g));
      return `${location.pathname}?${queryString.stringify({ group: groupIds })}`;
    }
    return location.pathname;
  };

  const groupList = (g: Group[]) =>
    g.map(group => (
      <div className={styles.TeacherSidebarGroup} key={group.name}>
        <Link
          to={getGroupLink(group)}
          className={classNames(styles.TeacherSidebarButton, {
            [styles.TeacherSidebarButtonActive]: selectedGroups.find(g => g.name === group.name),
          })}
          analyticsEvent={{
            category: 'navigation',
            action: 'select group',
            labels: {
              groupId: getStudentGroupId(group),
              selected: toggleGroups ? selectedGroups.join(',') : undefined,
              toggling: toggleGroups,
            },
          }}
          {...(!selectingGroup ? { 'data-prevent-hide': 'true' } : {})}
        >
          <div className={styles.TeacherSidebarClassSelect}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <span className={styles.TeacherSidebarButtonName}>{group.displayName}</span>
        </Link>
      </div>
    ));

  const starredGroupList = groups?.filter(g => starredGroups.has(g.name)) || [];
  const otherGroupList = groups?.filter(g => !starredGroups.has(g.name)) || [];

  return (
    <CollapsibleSidebar
      primary={
        <>
          <SidebarPrimaryLink name="Home" icon={faHome} to="/teacher" />
          <SidebarPrimaryLink
            name="Hand-in"
            customIcon={HandInIcon}
            to={getGroupContextLink(selectedGroups, '/teacher/classes/homework')}
            active={location.pathname === '/teacher/classes/homework'}
            preventHide={true}
          />
          <SidebarPrimaryLink
            name="Insights"
            customIcon={InsightsIcon}
            to={getGroupContextLink(selectedGroups, '/teacher/classes/insights')}
            active={location.pathname === '/teacher/classes/insights'}
            preventHide={true}
          />
          <SidebarPrimaryLink name="Reports" customIcon={ReportsIcon} to="/teacher/reports" />
          <SidebarPrimaryLink
            name="Leaderboard"
            icon={faCrown}
            active={location.pathname === '/teacher/leaderboard'}
            to={getGroupContextLink(selectedGroups, '/teacher/leaderboard')}
          />
          <SidebarPrimaryLink
            name="Live Monitoring"
            icon={faEye}
            to="/teacher/live"
            preventHide={true}
          />
          {isTeacherLibraryEnabled && (
            <SidebarPrimaryLink
              name="Library"
              icon={faBook}
              to={pathForView(View.TeacherLibrary)}
              preventHide={true}
              active={location.pathname.startsWith(pathForView(View.TeacherLibrary))}
            />
          )}
          {sparxStaffFeaturesEnabled && (
            <SidebarPrimaryLink
              name="EQM"
              icon={faAirFreshener}
              to={pathForView(View.EQM)}
              preventHide={true}
            />
          )}
          <TeacherMoreMenu selectedGroups={selectedGroups} />
        </>
      }
      secondary={
        <>
          {starredGroupList.length > 0 && (
            <>
              <h4>My classes</h4>
              {groupList(starredGroupList)}
            </>
          )}
          <h4>Select a class:</h4>
          {groupList(otherGroupList)}
        </>
      }
      secondarySelect={selectingGroup || !showGroups}
      secondaryHidden={!showGroups}
    />
  );
};

interface ITeacherMoreMenuProps {
  selectedGroups: Group[];
}

const TeacherMoreMenu = ({ selectedGroups }: ITeacherMoreMenuProps) => {
  const location = useLocation();

  const setAlert = useAlert();
  const clearAlert = useClearAlert();
  const navigate = useNavigate();

  const misSyncImport = useWondeImportEnabled();

  const { onMouseAway, onMouseEnter, openMenu, menuOpen } = useHoverClickMenu();

  const subscriptionManager = useBooleanFlag(Flags.SubscriptionManager);

  const openStudentDemo = () =>
    setAlert(
      <StudentDemoAlert
        submit={() => {
          navigate('/library');
          clearAlert();
        }}
      />,
    );

  return (
    <div
      className={classNames(styles.MoreMenuWrapper, menuOpen && styles.MoreMenuWrapperOpen)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseAway}
    >
      <SidebarPrimaryButton onClick={openMenu} name={`More`} customIcon={MoreIcon} />
      <div
        className={classNames(
          styles.MoreMenu,
          menuOpen && styles.MoreMenuOpen,
          misSyncImport && styles.MoreMenuMISSync,
        )}
      >
        <SidebarPrimaryLink
          name="Reading Test"
          icon={faListCheck}
          to={getGroupContextLink(selectedGroups, '/teacher/classes/readingtest')}
          active={location.pathname === '/teacher/classes/readingtest'}
          preventHide={true}
        />
        <SidebarPrimaryLink
          name="Student Manager"
          icon={faUsers}
          to={getGroupContextLink(selectedGroups, '/teacher/classes/students')}
          active={location.pathname === '/teacher/classes/students'}
          preventHide={true}
        />
        <SidebarPrimaryLink name="Staff Manager" icon={faUserGraduate} to="/teacher/staff" />
        <SidebarPrimaryButton name="Student Demo" icon={faUser} onClick={openStudentDemo} />
        {misSyncImport && (
          <SidebarPrimaryLink
            name="Import and Manage Classes"
            icon={faSyncAlt}
            to={'/teacher/mis-sync-import'}
            active={location.pathname === '/teacher/mis-sync-import'}
          />
        )}
        <SidebarPrimaryLink
          name="Reading Matters"
          icon={faBookOpenReader}
          to="/teacher/reading-matters"
          preventHide={true}
        />
        {subscriptionManager && (
          <SidebarPrimaryLink
            name="School Settings"
            icon={faGear}
            to="/teacher/school-settings"
            active={location.pathname.startsWith('/teacher/school-settings')}
          />
        )}
      </div>
    </div>
  );
};

const getGroupContextLink = (selectedGroups: Group[], path: string) => {
  if (selectedGroups.length !== 1) {
    return path;
  }
  return `${path}?group=${getStudentGroupId(selectedGroups[0])}`;
};

const StudentDemoAlert = ({ submit }: { submit: () => void }) => (
  <ConfirmAlert
    title="Enter student demo?"
    confirmText={'Enter student demo'}
    rejectText={'Cancel'}
    onConfirm={submit}
    confirmAnalytics={{ category: 'student demo', action: 'enter' }}
    rejectAnalytics={{ category: 'student demo', action: 'dismiss' }}
  >
    <p>
      By entering the student demo you will be able to experience <strong>Sparx Reader</strong> as
      if you were a student.
    </p>
  </ConfirmAlert>
);
